/**
 * useLogRead - what is new in the shared log since this viewer last looked,
 * and a scroller that stays at the foot only when it is at the foot (v9.7.0).
 *
 * - THE READ MARK (engine/log/readMark) is the viewer's own, per session
 *   (`logRead:<sessionId>` in the viewer prefs). A viewer who never looked
 *   starts at "now": what was said before they sat down is not news. While
 *   the log is WATCHED AT ITS FOOT (open, on the shared view, the page in
 *   front, scrolled to the bottom) a line that arrives is read at once, so
 *   no divider flickers under someone following along. Lines that arrived
 *   while it was NOT watched are read a moment after the viewer comes
 *   back, so the divider before the first of them is there to be seen.
 * - STICK TO THE FOOT: new lines scroll into view only when the viewer was
 *   already at the bottom. Scrolled up to read, the log stays put, and a
 *   pill says how many lines arrived (`jump` goes to them). It used to jump
 *   on every narrative line and never on a vox line.
 *
 * @param {object} args - sessionId, narrative, chat (vox messages), reader
 *   ({ uid, name }), scrollerRef, hidden (the lanes switched off), watching (the shared view is on screen and
 *   not rolled up), changeKey (anything that changes when lines arrive or
 *   the scroller is laid out again), now (the clock, for tests)
 * @returns {{ readAt, counts, unseen, unseenShown, atBottom, jump }} - `unseen` is every lane's
 *   (the roll-up toggle's count), `unseenShown` only the lanes the viewer shows (the pill's).
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { mergeLogItems } from '../engine/chat';
import { inLanes } from '../engine/log/lanes';
import { countsByLane, markAfter, unseenSince } from '../engine/log/readMark';
import { getViewerPrefs, setViewerPref, subscribeViewerPrefs } from '../utils/viewerPrefs';

const NO_LANES = [];

/** How close to the bottom still counts as "at the foot", in px. */
const FOOT_PX = 24;
/** How long the divider stays once the viewer has arrived at the new lines. */
export const READ_AFTER_MS = 2500;

const atFoot = (el) => el.scrollHeight - el.scrollTop - el.clientHeight < FOOT_PX;

/** Whether the scroller is at its foot, kept current, and the way back there. Sticks when it was there. */
function useFoot(scrollerRef, changeKey) {
  const [atBottom, setAtBottom] = useState(true);
  const stuck = useRef(true);
  useEffect(() => {
    const el = scrollerRef?.current;
    if (!el) return undefined;
    const onScroll = () => {
      stuck.current = atFoot(el);
      setAtBottom(stuck.current);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [scrollerRef]);
  // After new lines are laid out: follow them only if the viewer was at the foot.
  useLayoutEffect(() => {
    const el = scrollerRef?.current;
    if (el && stuck.current) el.scrollTop = el.scrollHeight;
  }, [scrollerRef, changeKey]);
  const jump = useCallback(() => {
    const el = scrollerRef?.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    stuck.current = true;
    setAtBottom(true);
  }, [scrollerRef]);
  return { atBottom, jump };
}

/** Whether the page is in front: a log on a background tab is not being watched. */
function usePageVisible() {
  const [visible, setVisible] = useState(() => !document.hidden);
  useEffect(() => {
    const onChange = () => setVisible(!document.hidden);
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);
  return visible;
}

/** The viewer's mark for this session, and the write that moves it. Never looked: it starts at now. */
function useMark(sessionId, now) {
  const key = `logRead:${sessionId ?? 'solo'}`;
  const prefs = useSyncExternalStore(subscribeViewerPrefs, getViewerPrefs, getViewerPrefs);
  const stored = prefs[key];
  const known = typeof stored === 'number' && Number.isFinite(stored);
  const move = useCallback((to) => setViewerPref(key, to), [key]);
  useEffect(() => {
    if (!known) move(now());
  }, [known, move, now]);
  return { readAt: known ? stored : Infinity, move };
}

/**
 * Move the mark as the log is read: at once while the viewer is following
 * along, a moment after they come back to lines they missed.
 */
function useReading({ attending, newest, readAt, move }) {
  // Caught up while attending: the next line to arrive is seen as it lands.
  const following = useRef(false);
  // The wait is for the VIEWER, not for the log to fall quiet: the newest time is read when the
  // wait ends, so a line arriving meanwhile neither starts it again nor is left behind.
  const latest = useRef(newest);
  latest.current = newest;
  const behind = newest > readAt;
  useEffect(() => {
    if (!attending) {
      following.current = false;
      return undefined;
    }
    if (!behind) {
      following.current = true;
      return undefined;
    }
    if (following.current) {
      move(latest.current);
      return undefined;
    }
    const t = setTimeout(() => move(latest.current), READ_AFTER_MS);
    return () => clearTimeout(t);
  }, [attending, behind, readAt, move]);
}

export default function useLogRead({
  sessionId,
  narrative,
  chat,
  reader,
  scrollerRef,
  watching,
  changeKey,
  hidden = NO_LANES,
  now = Date.now,
}) {
  const { atBottom, jump } = useFoot(scrollerRef, changeKey);
  const { readAt, move } = useMark(sessionId, now);
  const visible = usePageVisible();
  const items = useMemo(() => mergeLogItems(narrative ?? [], chat ?? []), [narrative, chat]);
  const unseen = useMemo(() => unseenSince(items, readAt, reader), [items, readAt, reader]);
  const counts = useMemo(() => countsByLane(unseen), [unseen]);
  // What the viewer will find on going to look: not the lines of a lane they switched off.
  const unseenShown = useMemo(() => inLanes(unseen, hidden).length, [unseen, hidden]);
  useReading({ attending: watching && visible && atBottom, newest: markAfter(items, readAt), readAt, move });
  return { readAt, counts, unseen: unseen.length, unseenShown, atBottom, jump };
}
